import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { Expense } from '../../../expense/expense.model';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../../shared/page-layout/page-form-section.component';
import { MyHrExpenseWorkspace } from '../shared/my-hr-workspace.models';
import { MyHrWorkspaceService } from '../shared/my-hr-workspace.service';
import { labelize, statusSeverity } from '../shared/my-hr-workspace.utils';

@Component({
  selector: 'app-my-hr-expenses',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    IftaLabelModule,
    InputNumberModule,
    InputGroupAddonModule,
    InputGroupModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    PageDataPanelComponent,
    PageFormSectionComponent
  ],
  providers: [MessageService],
  templateUrl: './my-hr-expenses.component.html',
  styleUrls: ['./my-hr-expenses.component.scss']
})
export class MyHrExpensesComponent implements OnInit {
  private readonly workspaceService = inject(MyHrWorkspaceService);
  private readonly permissionService = inject(PermissionService);
  private readonly messageService = inject(MessageService);

  readonly canViewExpenses = this.permissionService.hasPermission(app_permissions.expense.records.view);
  readonly canCreateExpenses = this.permissionService.hasPermission(app_permissions.expense.records.create);

  workspace?: MyHrExpenseWorkspace;
  saving = false;
  expenseForm: {
    itemDate: Date | null;
    account?: ChartOfAccounts;
    amount?: number | null;
    taxAmount?: number | null;
    description?: string;
    notes?: string;
    isReimbursable: boolean;
  } = {
    itemDate: new Date(),
    isReimbursable: true
  };

  ngOnInit(): void {
    this.load();
  }

  get monthTotal(): number {
    return (this.workspace?.expenses || []).reduce((total, expense) => total + Number(expense.totalAmount || 0), 0);
  }

  label(value?: string): string {
    return labelize(value);
  }

  statusSeverity(value?: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' {
    return statusSeverity(value);
  }

  save(submitForApproval = false): void {
    if (!this.canCreateExpenses || !this.workspace?.company || !this.workspace?.customer || !this.expenseForm.account || !this.expenseForm.amount || !this.expenseForm.description) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Expenses',
        detail: 'Account, amount, and description are required before saving.'
      });
      return;
    }

    const payload: Expense = {
      owningCompany: this.workspace.company,
      vendor: this.workspace.company,
      submittedBy: this.workspace.customer,
      submissionDate: this.expenseForm.itemDate || new Date(),
      totalAmount: Number(this.expenseForm.amount || 0) + Number(this.expenseForm.taxAmount || 0),
      currencyCode: this.workspace.currencyCode,
      description: this.expenseForm.description,
      notes: this.expenseForm.notes,
      items: [{
        itemDate: this.expenseForm.itemDate || new Date(),
        account: this.expenseForm.account,
        amount: Number(this.expenseForm.amount || 0),
        description: this.expenseForm.description,
        taxAmount: Number(this.expenseForm.taxAmount || 0),
        isReimbursable: this.expenseForm.isReimbursable
      }]
    } as Expense;

    this.saving = true;
    this.workspaceService.createExpense(payload, submitForApproval).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: submitForApproval ? 'Submitted' : 'Saved',
          detail: submitForApproval ? 'Expense claim sent for approval.' : 'Expense draft saved.'
        });
        this.expenseForm = {
          itemDate: new Date(),
          isReimbursable: true
        };
        this.workspaceService.requestRefresh();
        this.load();
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Expenses', detail: 'Expense claim could not be saved.' });
      },
      complete: () => {
        this.saving = false;
      }
    });
  }

  private load(): void {
    this.workspaceService.loadExpensesWorkspace().subscribe({
      next: workspace => {
        this.workspace = workspace;
      },
      error: () => {
        this.workspace = undefined;
      }
    });
  }
}
